import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { BoxFull, InputText, TransactionalSubheader } from "iuvity-ui-components/dist";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Button } from "iuvity-ui-components/dist/button";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useConfigurations } from "../../hooks/useConfigurations";
import { GlobalConfiguration } from "../../interfaces";
import { getGlobalConfigurationPageLabel } from "../../utils";
import { AppContext } from "../../context/index";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const formBlankValues = {
  functionality: [],
  widgets: [],
  key: [],
  value: "",
  type: [],
  description: "",
  status: "",
  releaseversion: [],
};

const columnNamesByKey = {
  key: "parameter",
  releaseversion: "version",
};

export const ConfigurationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, pageFilter, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          functionality: pageFilter?.functionality || [],
          widgets: pageFilter?.widgets || [],
          key: pageFilter?.key || [],
          value: pageFilter?.value || "",
          type: pageFilter?.type || [],
          description: pageFilter?.description || "",
          status: pageFilter?.status || "",
          releaseversion: pageFilter?.releaseversion || [],
        }
      : formBlankValues,
  });

  const { control, setValue, getValues } = form;

  const widgetsValues = useWatch({
    control,
    name: "widgets",
  });

  const keyValues = useWatch({
    control,
    name: "key",
  });

  const valueValues = useWatch({
    control,
    name: "value",
  });

  const typeValues = useWatch({
    control,
    name: "type",
  });

  const descriptionValues = useWatch({
    control,
    name: "description",
  });

  const statusValues = useWatch({
    control,
    name: "status",
  });
  
  const releaseversionValues = useWatch({
    control,
    name: "releaseversion",
  });

  const { data, configurationAction, reloadFn } = useConfigurations("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const customPageLabel = getGlobalConfigurationPageLabel(t, location.pathname);
  const pageName = location?.pathname.split("/").pop() || "";

  const columns = useMemo(() => {
    return [
      {
        field: "key",
        header: t("GlobalConfigurations.parameterColumnTitle"),
      },
      {
        field: "widgets",
        header: t("GlobalConfigurations.widgetsColumnTitle"),
      },
      {
        field: "value",
        header: t("GlobalConfigurations.valueColumnTitle"),
      },
      {
        field: "type",
        header: t("GlobalConfigurations.typeColumnTitle"),
      },
      {
        field: "description",
        header: t("GlobalConfigurations.descriptionColumnTitle"),
      },
      {
        field: "status",
        header: t("GlobalConfigurations.statusColumnTitle"),
      },
      {
        field: "releaseversion",
        header: t("GlobalConfigurations.versionColumnTitle"),
      },
      {
        field: "actions",
        header: t("GlobalConfigurations.actionsColumnTitle"),
      },
    ].filter((item: any) =>
      ["administrative", "globalConfiguration"].includes(
        (location?.pathname).split("/").pop() || ""
      )
        ? item
        : item?.field !== "widgets"
    );
  }, [t, location]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    key: { value: null, matchMode: FilterMatchMode.IN },
    widgets: { value: null, matchMode: FilterMatchMode.IN },
    value: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.IN },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    releaseversion: { value: null, matchMode: FilterMatchMode.IN },
  };

  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  // const [filterModalVisible, setFilterModalVisible] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const actionDetailFn = (config: GlobalConfiguration) => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: config,
    }));
    navigate(`${location.pathname}/detail`);
    window.scrollTo(0, 0);
  };

  const actionEditFn = (config: GlobalConfiguration) => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: config,
    }));
    navigate(`${location.pathname}/edit`);
    window.scrollTo(0, 0);
  };

  const actionDeleteFn = async (config: GlobalConfiguration) => {
    await configurationAction("DELETE", config.id);
    reloadFn();
  };

  const handleModalProps = async (config: GlobalConfiguration) => {
    const keyName = config.key.split(".").pop();
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("GlobalConfigurations.deleteModalTitle"),
      content: `${t("GlobalConfigurations.deleteModalContent1")} "${
        keyName
      }" ${t("GlobalConfigurations.deleteModalContent2")}`,
      onConfirm: () => actionDeleteFn(config),
    }));
  };

  const onGlobalClearFilters = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  useLayoutEffect(() => {
    if (data && pageFilter) {
      const { functionality } = form.getValues();
      setFilteredData(
        data?.filter((item) => {
          return functionality?.length > 0
            ? item?.widgets &&
                item?.widgets.some((item: any) => {
                  return functionality.includes(item);
                })
            : true;
        })
      );
    } else if (data) {
      setFilteredData(data);
    } else {
      setFilteredData(null);
    }
  }, [data, setFilteredData, form, pageFilter]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(
            `GlobalConfigurations.${
              Object.keys(columnNamesByKey).includes(col.field)
                ? (columnNamesByKey as any)[col.field]
                : col.field
            }ColumnTitle`
          ),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const [disableClearFilterButton, setDisableClearFilterButton] = useState<{
    widgets: boolean;
    key: boolean;
    value: boolean;
    type: boolean;
    description: boolean;
    status: boolean;
    guid: boolean;
    createdat: boolean;
    tenantcode: boolean;
    parent: boolean;
  }>({
    widgets: false,
    key: false,
    value: false,
    type: false,
    description: false,
    status: false,
    guid: false,
    createdat: false,
    tenantcode: false,
    parent: false
  });
  
  const onClearFiltersPerTable = (options: any, filtersFromSession: any) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      widgets: { value: options?.field === 'widgets' ? null : filtersFromSession?.widgets?.value, matchMode: FilterMatchMode.IN },
      key: { value: options?.field === 'key' ? null : filtersFromSession?.key?.value, matchMode: FilterMatchMode.IN },
      value: { value: options?.field === 'value' ? null : filtersFromSession?.value?.value, matchMode: FilterMatchMode.CONTAINS },
      type: { value: options?.field === 'type' ? null : filtersFromSession?.type?.value, matchMode: FilterMatchMode.IN },
      description: { value: options?.field === 'description' ? null : filtersFromSession?.description?.value, matchMode: FilterMatchMode.CONTAINS },
      status: { value: options?.field === 'status' ? null : filtersFromSession?.status?.value, matchMode: FilterMatchMode.EQUALS },
      releaseversion: { value: options?.field === 'releaseversion' ? null : filtersFromSession?.releaseversion?.value, matchMode: FilterMatchMode.IN },
    });

    // luego de dar click en limpiar, elimina los criterios de búsqueda.
    setValue(options?.field, '');
    setDisableClearFilterButton((prev: any) => ({
      ...prev,
      [options?.field]: true,
    }));
    setAppState((prev) => ({
      ...prev,
      pageFilter: {
        ...prev.pageFilter,
        [options.field]: '',
      }
    }));
  };

  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`GlobalConfigurations.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`GlobalConfigurations.${options.field}PlaceholderLabel`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const multiselectFilterTemplate = (options: any) => {
    const arrayOriginal = data
    ?.map((config) => ({
      ...config,
      key: config.key.split(".").pop(),
      widgets: config?.widgets?.join(", "),
      type: t(
        `GlobalConfigurations.${
          config?.type === "number" ? "numeric" : config?.type
        }OptionLabel`
      ),
      status: t(
        `GlobalConfigurations.${config?.status.toLowerCase()}StatusLabel`
      )
    }))
    ?.map((item: any) => (
      {
        label: Array.isArray(item[`${options.field}`]) ? item[`${options.field}`][0] : item[`${options.field}`], 
        value: Array.isArray(item[`${options.field}`]) ? item[`${options.field}`][0] : item[`${options.field}`]
      }))
    ?.filter((item: { label: string, value: string }) => item.label)
    ?.sort((a: any, b: any) => a?.label?.localeCompare(b?.label));
    
    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
      duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          // rules={PermissionsFormSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`GlobalConfigurations.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`GlobalConfigurations.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("GlobalConfigurations.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Permissions.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            (options?.field === 'widgets' && widgetsValues?.length > 0) ||
            (options?.field === 'key' && keyValues?.length > 0) ||
            (options?.field === 'value' && valueValues?.length > 0) ||
            (options?.field === 'type' && typeValues?.length > 0) ||
            (options?.field === 'description' && descriptionValues?.length > 0) ||
            (options?.field === 'status' && statusValues?.length > 0) ||
            (options?.field === 'releaseversion' && releaseversionValues?.length > 0)
          )
        }
        onClick={()=>{
          options.filterApplyCallback();
          setAppState((prev: any)=>({
            ...prev,
            pageFilter: getValues(),
          }));
          setDisableClearFilterButton((prev: any) => ({
            ...prev,
            [options?.field]: false,
          }));
        }}
      />
    )
  };

  const filterClearTemplate = (option: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Permissions.clearFilterLabel")}
        variant="p-button-text"
        disabled={(filtersFromSession?.hasOwnProperty('global')
          ? !filtersFromSession[option?.field]?.value : true)
          // @ts-ignore
          || disableClearFilterButton[option?.field]}
        onClick={() => onClearFiltersPerTable(option, filtersFromSession)}
      />
    )
  };

  const statuses = [
    `${t("GlobalConfigurations.activeStatusLabel")}`,
    `${t("GlobalConfigurations.inactiveStatusLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
            name={options.field}
            control={control}
            render={({ field }) => {
              field.onChange = (e) => {
                setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
                options.filterCallback(e.value);
              };
              return (
                <>
                  <label htmlFor={options.field} className="block lb mt-2 mb-3">
                    {t(`GlobalConfigurations.${options.field}Label`)}
                  </label>
                  {/* // @ts-ignore */}
                  <Dropdown 
                    options={statuses}
                    itemTemplate={statusItemTemplate}
                    placeholder={t(`GlobalConfigurations.${options.field}PlaceholderLabel`)}
                    className="p-column-filter mb-2"
                    showClear={false}
                    {...field} 
                    />
                </>
              );
            }}
          />
      
    )
  };

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {customPageLabel}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={customPageLabel}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: customPageLabel,
              style: { fontWeight: "bold" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>
            {[
              "iuvinow",
              "iuvisales",
              "iuviprofiler",
              "administrative",
              "mobile"
            ].includes(pageName)
              ? t(
                  `GlobalConfigurations.${pageName}GlobalConfigurationDescription`
                )
              : t("GlobalConfigurations.globalConfigurationDescription")}
          </span>
          {!["iuvinow", "iuvisales", "iuviprofiler", "administrative", "mobile"].includes(
            pageName
          ) && (
            <span>
              {t("GlobalConfigurations.globalConfiguration2Description")}
            </span>
          )}
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-plus",
            label: t("GlobalConfigurations.newConfigurationLink"),
            style: {
              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
            },
            onClick: () => navigate(`${location.pathname}/new`),
          },
        ])}
      />

      {/* {["globalConfiguration", "administrative"].includes(pageName) &&
        formOptions &&
        formOptions?.functionality.length > 0 && (
          <BoxFull className="mt-2 py-0 px-0" variant="tertiary">
            <div className="tablefilter-mc">
              <Button
                label={t("Translations.filterBtnLabel1")}
                variant="p-button"
                icon="iu iu-icon-filter"
                onClick={() => {
                  window.scrollTo({ top: 0 });
                  document.body.style.overflowY = "hidden";
                  setFilterModalVisible(true);
                }}
                disabled={!formOptions}
              />
              <Button
                label={t("Translations.clearFilterBtnLabel")}
                variant="p-button"
                icon="iu iu-icon-error"
                onClick={onClearFilters}
                disabled={!formOptions}
              />
            </div>
          </BoxFull>
        )} */}

      <BoxFull className="dt-c my-4">
        {filteredData ? (
          <DataTable
            header={
              <TableHeader
                title={
                  t("GlobalConfigurations.globalConfigurationTableTitle") +
                  ` - ${customPageLabel}`
                }
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={reloadFn}
                onClearFiltersColumn={onGlobalClearFilters}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            filters={filters}
            value={filteredData.map((config) => ({
              ...config,
              key: config.key.split(".").pop(),
              widgets: config?.widgets?.join(", "),
              type: t(
                `GlobalConfigurations.${
                  config?.type === "number" ? "numeric" : config?.type
                }OptionLabel`
              ),
              status: t(
                `GlobalConfigurations.${config?.status.toLowerCase()}StatusLabel`
              ),
              actions: (
                <Actions
                  enableDelete={config.iscustomconfiguration || false}
                  onDetail={() => actionDetailFn(config)}
                  onEdit={() => actionEditFn(config)}
                  onDelete={() => handleModalProps(config)}
                  visibleObject={{ ...permissiondInfoByRole }}
                />
              ),
            }))}
            scrollable
            resizableColumns
            globalFilterFields={['key', 'widgets', 'value', 'type', 'description', 'status', 'releaseversion']}
            filterDisplay="menu"
            stateStorage="session"
            stateKey="filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                  ? { justifyContent: "center" }
                  : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8.3rem" : "14rem",
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={
                  ((col.field === "value" || col.field === "description") && filterTemplate) ||
                  ((col.field === "key" || col.field === "widgets" || col.field === "type" ||  col.field === "releaseversion") && multiselectFilterTemplate) ||
                  ((col.field === "status") && statusFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                body={(rowData) => allFieldsBodyTemplate(rowData, col.field, 'iscustomconfiguration')}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      {/* <FilterDialog
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        data={data}
        setData={setFilteredData}
        onClearFilters={onClearFilters}
        form={form}
        formOptions={formOptions}
      /> */}

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
